import { Link } from "react-router-dom";
import type { PropertyRow } from "../../rdf/types";

/**
 * Generic (non-SHACL) fact table for a term — every remaining
 * predicate/object triple where the term is the subject, after label/comment
 * (shown in the header) are excluded. Values that are themselves IRIs link
 * into the Explorer at the Classes tab regardless of the target's real
 * kind — the tab only affects the tree's default listing, not whether a
 * `?term=` deep link resolves, so this stays correct without needing to
 * classify every linked value up front.
 */
export default function PropertyTable({ rows }: { rows: PropertyRow[] }) {
  if (rows.length === 0) {
    return (
      <p className="text-sm text-[var(--oe-text-muted)]">No other properties.</p>
    );
  }

  return (
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="border-b border-[var(--oe-border)] text-left text-xs uppercase tracking-wide text-[var(--oe-text-muted)]">
          <th className="w-1/3 py-1.5 pr-4">Property</th>
          <th className="py-1.5">Value</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr
            key={`${row.predicateIri}-${index}`}
            className="border-b border-[var(--oe-border)]/60"
          >
            <td className="py-1.5 pr-4 align-top">
              <span title={row.predicateIri}>
                {row.predicateLabel ?? row.predicatePrefixed}
              </span>
            </td>
            <td className="py-1.5 align-top">
              {row.valueIri ? (
                <Link
                  to={`/explore/classes?term=${encodeURIComponent(row.valueIri)}`}
                  className="text-[var(--oe-accent)] hover:underline"
                >
                  {row.value}
                </Link>
              ) : (
                <span className="break-words">{row.value}</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
